import React from "react";
import Head from "next/head";
import { seoConfig } from "../../config/seo.config";

type Props = {
  title: string;
  description: string;
  link: string;
  image?: string;
  type?: string;
  keywords?: string;
  structuredData?: object;
};

const HelmetComponent = ({ 
  title, 
  description, 
  link, 
  image = "https://turfbooking.in/TBlogo.png",
  type = "website",
  keywords = "turf booking online, book cricket turf, football turf booking, box cricket booking, sports ground rental, turf booking india",
  structuredData
}: Props) => {
  const fullTitle = title.includes("TurfBooking") ? title : `${title} | TurfBooking.in`;
  const siteName = "TurfBooking.in";
  // Use centralized SEO config to ensure consistent canonical URLs (always turfbooking.in)
  const baseUrl = seoConfig.baseUrl;
  const canonicalUrl = link.startsWith("http") ? link : `${baseUrl}${link}`;
  const ogImage = image.startsWith("http") ? image : `${baseUrl}${image}`;

  return (
    <Head>
      {/* Basic Meta Tags */}
      <meta charSet="utf-8" />
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <link rel="canonical" href={canonicalUrl} />
      
      {/* Open Graph Tags */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:locale" content="en_IN" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:site" content="@TurfBooking" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="author" content="TurfBooking.in" />
      <meta name="geo.region" content="IN" />
      <meta name="geo.country" content="India" />
      
      {/* Structured Data (JSON-LD) */}
      {structuredData && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
        />
      )}
    </Head>
  );
};

export default HelmetComponent;
